import { useContext, useState, useEffect } from "react";
import { AlquileresContext } from "../../../../context/AlquileresContext";
import { ActividadContext } from "../../../../context/ActividadContext";
import { saveConfigAlquileres } from "../../../../application/configuracion";
import SignaturePad from "../../../../components/SignaturePad/SignaturePad";
import { FaSave, FaCheck, FaSignature, FaTrash, FaPen, FaUpload } from "react-icons/fa";

// Lee una imagen y la reduce a un data URL PNG liviano (firma para el recibo).
// Mantiene la transparencia y limita el ancho para que entre holgado en el
// documento de config de Firestore (límite 1MB).
const imageToDataUrl = (file, maxDim = 500) =>
  new Promise((resolve, reject) => {
    if (!file || !file.type?.startsWith("image/")) {
      return reject(new Error("Seleccioná un archivo de imagen"));
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        if (width >= height) {
          height = Math.round((height * maxDim) / width);
          width = maxDim;
        } else {
          width = Math.round((width * maxDim) / height);
          height = maxDim;
        }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("No se pudo leer la imagen"));
    };
    img.src = url;
  });

function GeneralConfig() {
  const { config, refreshConfig } = useContext(AlquileresContext);
  const { registrarActividad } = useContext(ActividadContext);
  const [form, setForm] = useState({
    comisionPorcentaje: 5,
    diasAlertaVencimiento: 30,
    firmaImg: "",
    firmaAclaracion: "Agustín Plorutti",
    reciboDomicilio: "",
    reciboLocalidad: "",
    reciboCuit: "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [firmaError, setFirmaError] = useState("");
  const [dibujando, setDibujando] = useState(false);

  useEffect(() => {
    setForm({
      comisionPorcentaje: config.comisionPorcentaje || 5,
      diasAlertaVencimiento: config.diasAlertaVencimiento || 30,
      firmaImg: config.firmaImg || "",
      firmaAclaracion: config.firmaAclaracion ?? "Agustín Plorutti",
      reciboDomicilio: config.reciboDomicilio || "",
      reciboLocalidad: config.reciboLocalidad || "",
      reciboCuit: config.reciboCuit || "",
    });
  }, [config]);

  const handleFirmaFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite re-seleccionar el mismo archivo
    if (!file) return;
    setFirmaError("");
    try {
      const dataUrl = await imageToDataUrl(file);
      setForm((prev) => ({ ...prev, firmaImg: dataUrl }));
    } catch (err) {
      setFirmaError(err.message || "No se pudo cargar la firma");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const firmaCambio = form.firmaImg !== (config.firmaImg || "");
    setSaving(true);
    try {
      await saveConfigAlquileres({
        comisionPorcentaje: Number(form.comisionPorcentaje),
        diasAlertaVencimiento: Number(form.diasAlertaVencimiento),
        firmaImg: form.firmaImg || "",
        firmaAclaracion: form.firmaAclaracion || "",
        reciboDomicilio: form.reciboDomicilio || "",
        reciboLocalidad: form.reciboLocalidad || "",
        reciboCuit: form.reciboCuit || "",
      });
      await refreshConfig();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      if (firmaCambio) {
        registrarActividad("FIRMA_ACTUALIZADA", {
          mensaje: "Se actualizó la firma usada en los recibos",
          entidadTipo: "CONFIG",
        });
      }
    } catch (error) {
      console.error("Error al guardar configuracion:", error);
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    "w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent";

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Porcentaje de Comision por Defecto (%)
          </label>
          <input
            type="number"
            value={form.comisionPorcentaje}
            onChange={(e) => setForm({ ...form, comisionPorcentaje: e.target.value })}
            className={inputClass}
            min="0"
            max="100"
            step="0.5"
          />
          <p className="text-xs text-gray-400 mt-1">
            Este porcentaje se aplicara automaticamente al crear nuevos contratos.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Dias de Alerta por Vencimiento
          </label>
          <input
            type="number"
            value={form.diasAlertaVencimiento}
            onChange={(e) => setForm({ ...form, diasAlertaVencimiento: e.target.value })}
            className={inputClass}
            min="1"
            max="365"
          />
          <p className="text-xs text-gray-400 mt-1">
            Los contratos que venzan dentro de estos dias se mostraran como alerta.
          </p>
        </div>
      </div>

      {/* Firma para recibos */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 space-y-4">
        <div className="flex items-center gap-2">
          <FaSignature className="text-primary" />
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Firma para recibos</h3>
        </div>
        <p className="text-xs text-gray-400 -mt-2">
          Se incluye en los recibos de cobro que se envían por WhatsApp. Podés dibujarla
          en pantalla o subir una imagen (fondo transparente o blanco).
        </p>

        {dibujando ? (
          <SignaturePad
            onSave={(dataUrl) => {
              setForm((prev) => ({ ...prev, firmaImg: dataUrl }));
              setFirmaError("");
              setDibujando(false);
            }}
            onCancel={() => setDibujando(false)}
          />
        ) : (
          <div className="flex items-start gap-4">
            <div className="w-44 h-24 shrink-0 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center bg-gray-50 dark:bg-gray-700/40 overflow-hidden">
              {form.firmaImg ? (
                <img src={form.firmaImg} alt="Firma" className="max-h-full max-w-full object-contain" />
              ) : (
                <span className="text-xs text-gray-400">Sin firma</span>
              )}
            </div>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setDibujando(true)}
                className="w-full inline-flex items-center gap-2 px-3 py-2 text-sm bg-primary hover:bg-secondary text-white rounded-lg transition-colors"
              >
                <FaPen className="w-3.5 h-3.5" />
                {form.firmaImg ? "Dibujar de nuevo" : "Dibujar firma"}
              </button>
              <label className="w-full inline-flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg cursor-pointer transition-colors">
                <FaUpload className="w-3.5 h-3.5" />
                Subir imagen
                <input type="file" accept="image/*" onChange={handleFirmaFile} className="hidden" />
              </label>
              {form.firmaImg && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, firmaImg: "" })}
                  className="w-full inline-flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                >
                  <FaTrash className="w-3.5 h-3.5" /> Quitar
                </button>
              )}
              {firmaError && <p className="text-xs text-red-500">{firmaError}</p>}
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Aclaración (debajo de la firma)
          </label>
          <input
            type="text"
            value={form.firmaAclaracion}
            onChange={(e) => setForm({ ...form, firmaAclaracion: e.target.value })}
            className={inputClass}
            placeholder="Ej: Agustín Plorutti"
          />
        </div>

        <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
          <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-1">Datos de la inmobiliaria (recibo)</p>
          <p className="text-xs text-gray-400 mb-3">
            Se completan por defecto en los recibos. Igual podés editarlos al generar cada uno.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Domicilio</label>
              <input
                type="text"
                value={form.reciboDomicilio}
                onChange={(e) => setForm({ ...form, reciboDomicilio: e.target.value })}
                className={inputClass}
                placeholder="Ej: Arenales 192"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Localidad</label>
              <input
                type="text"
                value={form.reciboLocalidad}
                onChange={(e) => setForm({ ...form, reciboLocalidad: e.target.value })}
                className={inputClass}
                placeholder="Ej: Chascomús"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CUIT</label>
              <input
                type="text"
                value={form.reciboCuit}
                onChange={(e) => setForm({ ...form, reciboCuit: e.target.value })}
                className={inputClass}
                placeholder="Opcional"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className={`inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${
            saved ? "bg-green-500 text-white" : "bg-primary hover:bg-secondary text-white"
          }`}
        >
          {saved ? (
            <><FaCheck className="w-4 h-4" /> Guardado</>
          ) : saving ? (
            "Guardando..."
          ) : (
            <><FaSave className="w-4 h-4" /> Guardar</>
          )}
        </button>
      </div>
    </form>
  );
}

export default GeneralConfig;
